import gql from 'graphql-tag';
import { find, get } from 'lodash';
import { graphql } from 'react-apollo';

export const GET_PROVIDER = gql`
    query getProvider($providerId: String, $directoryId: String) {
        provider(id: $providerId, directoryId: $directoryId) {
            id
            approvedFullName
            title
            firstName
            lastName
            prenominal
            circlePictureUrl
            frontEndCirclePictureUrl
            frontEndSquarePictureUrl
            showPhotoWithCircleCss
            squarePictureUrl
            averageRating
            defaultProcedureId
            profileUrl
            mayChargeAdditionalCopay
            hasVirtualLocations
            nameInSentence
            shortNameInSentence
            practices {
                id
                isFeePerBooking
            }
            selfPaySettings {
                initialSessionCost
                initialSessionLength
                ongoingSessionLength
                ongoingSessionMinCost
                ongoingSessionMaxCost
                isSlidingScaleOffered
                helpFileInsuranceClaim
                chargeInsuranceClaimDifference
            }
            specialties {
                id
                name
            }
            relevantSpecialty {
                id
                name
            }
            locations {
                id
                monolithId
                practiceId
                address1
                address2
                city
                state
                zipCode
                phone
                phoneExt
                isVirtual
                virtualVisitType
                latitude
                longitude
            }
            procedures {
                id
                name
                show911Notice
                insuranceMayNotCover
            }
            popularProcedures {
                id
                name
                show911Notice
                insuranceMayNotCover
            }
        }
    }
`;

export function providerGqlOptions(ownProps) {
    return {
        variables: {
            providerId: ownProps.providerId,
            directoryId: '-1',
        },
    };
}

export const withProviderMapping = (ownProps, data, getRawProvider = false) => {
    const provider = get(data, 'provider', { relevantSpecialty: {} });
    const providerLocations = get(data, 'provider.locations', []);
    const providerProcedures = get(data, 'provider.procedures', []);
    const practices = get(data, 'provider.practices', []);
    const providerLocation = find(
        providerLocations,
        (location) => location.monolithId === ownProps.locationId
    );

    let firstVirtualLocation;

    if (providerLocation && providerLocation.state) {
        firstVirtualLocation = find(
            providerLocations,
            (location) =>
                location.isVirtual && location.state === providerLocation.state
        );
    }

    const practice = find(
        practices,
        (prac) => get(providerLocation, 'practiceId') === prac.id
    );
    const visitReason = find(
        providerProcedures,
        (procedure) => procedure.id === ownProps.procedureId
    );

    return {
        visitReason: visitReason ? visitReason.name : 'N/A',
        appointmentTime: ownProps.appointmentTime,
        location: providerLocation,

        /* The monolith uses "professional" to refer to what we refer to
         * everywhere else as "provider" (the person seeing the patient).
         * Here we also use that convention in BWA, but it's inconsistent
         * with all our other front-end React code including some code that
         * is shared between BWA and PWA, so we're slowly moving away form this
         * to the standard naming convention. Eventually we should get rid of this
         * "professional" naming altogether.
         */

        professional: {
            approvedFullName: get(provider, 'approvedFullName'),
            name: `${provider.firstName} ${provider.lastName}, ${provider.title}`,
            firstName: `${provider.firstName}`,
            frontEndCirclePictureUrl: get(
                provider,
                'frontEndCirclePictureUrl',
                ''
            ),
            lastName: `${provider.lastName}`,
            title: `${provider.title}`,
            specialty: get(provider.relevantSpecialty, 'name'),
            specialtyId: get(provider.relevantSpecialty, 'id'),
            defaultProcedureId: provider.defaultProcedureId,
            rating: provider.averageRating,
            showPhotoWithCircleCss: provider.showPhotoWithCircleCss,
            image: provider.showPhotoWithCircleCss
                ? provider.squarePictureUrl
                : provider.circlePictureUrl,
            profileUrl: provider.profileUrl,
            isFeePerBooking: get(practice, 'isFeePerBooking', false),
            hasVirtualLocations: get(provider, 'hasVirtualLocations', false),
            specialties: get(provider, 'specialties', []),
            firstVirtualLocation,
            providerLocation,
            nameInSentence: get(provider, 'nameInSentence'),
            selfPaySettings: get(provider, 'selfPaySettings', {}),
        },
        provider: provider,
    };
};

export const withProvider = graphql(GET_PROVIDER, {
    skip: (ownProps) => {
        return !ownProps.providerId;
    },
    options: providerGqlOptions,
    props: ({ ownProps, data }) => {
        if (data.loading) return { providerLoading: true };
        if (data.error) {
            return { hasErrors: true };
        }

        return withProviderMapping(ownProps, data);
    },
});

export default withProvider;
